import BottomCard from '@/components/elements/Card/Card';
import { Card } from '@/components/elements/cards';
import { Button, LinkButton } from '@/components/elements/forms/buttons';
import Header from '@/components/elements/Header/Header';
import { classnames, getTotalPrice, servicesWithExtraTimeAddOns, shouldShowFrom, showPrices } from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { getExtraTimeRequirementForUser } from '@/helpers/userHelper';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { BookState, EXTRA_TIME_REQUIREMENT } from '@/types/state/book';
import { CampaignProduct, PlaceEmployee } from '@/types/state/shared';
import { Fragment, useCallback, useContext, useMemo } from 'react';
import Modal from '../../redesign/Modal/Modal';
import {
  getNewExtraTimeAddOns,
  getPickedEmployee,
  getRecommendedServices,
  hasMultipleEmployees,
  ScrollToTopElement,
  showExtraTimeView,
} from '../BookAppointment.helpers';
import { BookAppointmentContext, useBookAppointmentContext } from '../BookAppointment.hooks';
import { ANY_AMPLOYEE } from '../BookAppointments.constants';
import ServiceItem from '../shared/ServiceItem';
import Summary, { SummaryProps } from '../shared/Summary';

const baseTranslationKey = 'components.modules.modals.BookAppointment.views.SummaryView';

const SummaryView = () => {
  const { isMobileView } = useMobileView();
  const context = useBookAppointmentContext();
  const { employee, place, services: pickedServices, campaigns } = useAppSelector((state) => state.book) as BookState;

  const pickedProducts = useMemo(() => {
    if (!campaigns) return [];
    const products: CampaignProduct[] = [];

    campaigns.forEach((campaign) => {
      if (campaign.type === 7 && campaign.publicationType === 2 && campaign.products?.length > 0) {
        products.push(...campaign.products);
      }
    });
    return products;
  }, [campaigns]);

  const recommendedServices = useMemo(
    () => getRecommendedServices(pickedServices || [], getPickedEmployee(employee, place.employees) ?? ANY_AMPLOYEE),
    [pickedServices, employee],
  );

  const pickedEmployee: PlaceEmployee | undefined = (() => {
    if (place.about?.settings?.hideEmployees) {
      return undefined;
    }

    const picked = getPickedEmployee(employee, place.employees);
    return picked ?? ANY_AMPLOYEE;
  })();

  const [pickedAddonServices, pickedMainServices] = [
    (pickedServices || []).filter((service) => service?.about?.settings?.isAddOn),
    (pickedServices || []).filter((service) => !service?.about?.settings?.isAddOn),
  ];

  const canPickRecommendedServices = recommendedServices.length > 0 && pickedMainServices.length <= 1;

  const handleRemoveService = useCallback(
    (serviceId: number) => {
      context.onRemoveService(serviceId);
    },
    [context],
  );

  const handleGoBack = useCallback(() => {
    context.onGoBack('summary');
  }, [context]);

  const summaryProps: SummaryProps = {
    pickedEmployee,
    pickedServices,
    pickedProducts,
    showPickedEmployee: hasMultipleEmployees(place) && !Boolean(place.about?.settings?.hideEmployees),
    showPickedServices: true,
    showPickedProducts: true,
    showAddAdditionalService: !pickedAddonServices.length,
    onRemoveService: handleRemoveService,
    onShowAvailableServices: handleShowAvailableServices,
  };

  function handleShowAvailableServices() {
    bookingFlowTrackingSession.addService('initiated');
    context.updateView('pickServices');
  }

  return (
    <Modal.Content style={isMobileView ? {} : { padding: 0 }} floating={!isMobileView} gradientBackground>
      <ScrollToTopElement />
      {isMobileView && <Modal.Header title={_s(`${baseTranslationKey}.title`)} onBack={handleGoBack} />}
      <div className={classnames(!isMobileView && 'p-lg space-y-lg')}>
        {!isMobileView && <Modal.Header title={_s(`${baseTranslationKey}.title`)} onBack={handleGoBack} />}
        <div className={`flex flex-col ${isMobileView ? 'px-lg' : ''}`}>
          {!isMobileView && (
            <div className="px-md">
              <Summary {...summaryProps} />
            </div>
          )}
          {isMobileView && (
            <Card className="p-lg !m-0 rounded-lg !border-0 bg-white">
              <Summary {...summaryProps} />
            </Card>
          )}
          {canPickRecommendedServices && (
            <>
              <div className="pt-xxl">
                <Header label={_s(`${baseTranslationKey}.addons.header`)} size="md" />
              </div>
              {recommendedServices.map((service, idx) => (
                <ServiceItem
                  key={service.id}
                  service={service}
                  isRecommended={true}
                  underline={recommendedServices.length - 1 !== idx}
                />
              ))}
            </>
          )}
        </div>
      </div>

      <Fragment>
        {/* spacer to avoid elements going under fixed bottom card */}
        {isMobileView && <div className="h-[128px]" />}
        {/* spacer to avoid elements going under fixed bottom card */}
        <div className={classnames(isMobileView ? 'fixed left-0 right-0 mt-auto' : 'sticky', 'bottom-0')}>
          <BottomCard className="pb-lg h-auto w-full bg-white">
            <Footer />
          </BottomCard>
        </div>
      </Fragment>
    </Modal.Content>
  );
};

const Footer = () => {
  const props = useContext(BookAppointmentContext);
  const {
    employee,
    place,
    services: pickedServices,
    campaigns,
    extraTimeRequirement,
  } = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const extraTimeRequiredForUser = extraTimeRequirement ?? getExtraTimeRequirementForUser(user, place?.id);
  const pickedEmployee = getPickedEmployee(employee, place.employees);
  const { serviceCount } = servicesWithExtraTimeAddOns(pickedServices);
  const serviceCountText = _s(`${baseTranslationKey}.cta.description`, { count: serviceCount });
  const priceListId = pickedEmployee?.about?.priceListId ?? 0;

  const pickedProducts = (campaigns || []).flatMap((campaign) => campaign.products ?? []);

  const [productPrices, productPricesWithoutCampaigns] = [
    /**
     * if no originalPrice exist then the product is not discounted
     * price is always the price that the customer pays, whether it's discounted or not
     */
    pickedProducts.map((p) => ({ price: p.price })),
    pickedProducts.filter((p) => p.originalPrice !== undefined).map((p) => ({ price: p.originalPrice })),
  ];

  const total = getTotalPrice(
    pickedServices,
    place,
    priceListId,
    {},
    0,
    1,
    true,
    campaigns,
    false,
    undefined,
    undefined,
    undefined,
    productPrices,
  );
  const totalWithoutCampaigns = getTotalPrice(
    pickedServices,
    place,
    priceListId,
    {},
    0,
    1,
    true,
    [],
    false,
    undefined,
    undefined,
    undefined,
    productPricesWithoutCampaigns,
  );
  const hasCampaignPrice = total !== totalWithoutCampaigns;
  const isPriceVariable =
    shouldShowFrom(pickedServices, place) && total !== _s('variablePrice') && total !== _s('freePrice');
  const shouldShowPrices = showPrices(place, pickedServices);
  const shouldShowCTA = pickedServices.length > 0;
  const shouldShowExtraTime = showExtraTimeView(
    pickedServices ?? [],
    pickedEmployee ?? ANY_AMPLOYEE,
    extraTimeRequiredForUser,
  );
  const calendarPathname = (() => {
    if (!shouldShowCTA) return '';
    return `/boka-tjanst/${place.about.slug ? place.about.slug + '-' : ''}${place.id}/${pickedServices[0].about.slug}-${
      pickedServices[0].id
    }`;
  })();

  const handleOnCTAClick = () => {
    if (!shouldShowExtraTime) {
      if (extraTimeRequiredForUser === EXTRA_TIME_REQUIREMENT.NeedsExtraTime) {
        const extraTimeAddOns = getNewExtraTimeAddOns(pickedServices ?? [], pickedEmployee ?? ANY_AMPLOYEE);
        if (extraTimeAddOns.length > 0) {
          props.onPickExtraTime(extraTimeRequiredForUser);
          extraTimeAddOns.forEach((item) => {
            props.onPickService(item, true);
          });
        }
      }
      bookingFlowTrackingSession.searchTime();
      props.close();
    } else {
      props.updateView('pickExtraTime');
    }
  };

  const dealCampaign = campaigns.find((campaign) => campaign.type === 7 && campaign.publicationType === 2);
  const isSistaminutenFlow = Boolean(place.lastMinuteDiscount);
  const isDealsFlow = dealCampaign !== undefined;

  let search = undefined;

  if (isSistaminutenFlow) {
    search = '?sistaminuten=1';
  } else if (isDealsFlow) {
    search = '?dealId=' + dealCampaign.id;
  }

  const to = shouldShowCTA ? { pathname: calendarPathname, search, state: { fromLanding: true } } : undefined;

  return (
    <Fragment>
      {shouldShowPrices && (
        <div className="pb-sm flex items-center justify-between">
          <span className="text-black-900 text-m font-semibold">{serviceCountText}</span>
          <span className="text-black-900 text-m font-semibold">
            {isPriceVariable ? `${_s('from')} ` : ''}
            {hasCampaignPrice ? (
              <span className="text-highlight-700">
                {total} <span className="text-black-600 line-through">({totalWithoutCampaigns})</span>
              </span>
            ) : (
              total
            )}
          </span>
        </div>
      )}
      {shouldShowExtraTime && (
        <Button data-cy="goToExtraTimePickerButton" onClick={handleOnCTAClick} variant="primary" size="md">
          {_s(`${baseTranslationKey}.cta.extraTime.label`)}
        </Button>
      )}
      {!shouldShowExtraTime && (
        <LinkButton data-cy="goToWeekPickerButton" onClick={handleOnCTAClick} variant="primary" size="md" to={to}>
          {_s(`${baseTranslationKey}.cta.label`)}
        </LinkButton>
      )}
    </Fragment>
  );
};

export default SummaryView;
